import { FilterFilled, HomeOutlined } from '@ant-design/icons'
import { Horizontal, GraviButton } from '@gravitate-js/excalibrr'
import { IconGasStation, IconBuildingFactory2, IconSettings } from '@tabler/icons-react'
import { Dropdown } from 'antd'
import { useMemo } from 'react'
import type { PointsOfInterestResponse } from '../../../types'
import type { MapFilters, MapSettings, PoiTypes } from '../types'
import { getSearchOptions, isFilterActive } from '../util'
import { FilterDropdown } from './FilterDropdown'
import { SettingsDropdown } from './SettingsDropdown'

type MapHeaderProps = {
  pointsOfInterest: PointsOfInterestResponse // Adjust type as needed
  filters: MapFilters
  settings: MapSettings | null
  handleFilterChange: <T extends PoiTypes>(
    poiType: T,
    filterKey: keyof MapFilters[T],
    value: MapFilters[T][keyof MapFilters[T]]
  ) => void
  handleLocationSearch: (coords: [number, number]) => void
  handleUpdateMapSettings: <K extends keyof MapSettings>(key: K, value: MapSettings[K]) => void
}

export function MapHeader({
  pointsOfInterest,
  filters,
  settings,
  handleFilterChange,
  handleLocationSearch,
  handleUpdateMapSettings,
}: MapHeaderProps) {
  const buttonStyle = {
    borderRadius: '8px',
    boxShadow: '0 2px 2px rgba(60,64,67,0.3),0 1px 3px 1px rgba(60,64,67,0.15)',
    border: 'none',
    overflow: 'hidden',
  }

  const searchOptions = getSearchOptions(pointsOfInterest)
  const isActive = useMemo(() => {
    return {
      sites: isFilterActive(filters, 'sites'),
      terminals: isFilterActive(filters, 'terminals'),
      depots: isFilterActive(filters, 'depots'),
    }
  }, [filters])

  return (
    <Horizontal style={{ position: 'absolute', top: '.5em', left: '2em', zIndex: 1000 }} className='p-2' gap={10}>
      <Dropdown
        trigger={['click']}
        overlay={() => (
          <FilterDropdown
            name='sites'
            filterOptions={searchOptions}
            handleFilterChange={handleFilterChange}
            handleLocationSearch={handleLocationSearch}
            areFiltersActive={isActive.sites}
            poiFilters={filters.sites}
          />
        )}
        placement='bottomLeft'
        className={isActive.sites ? 'active-filter' : ''} // Add active class if filter is active
        destroyPopupOnHide={true} // Ensure dropdown is destroyed when hidden
      >
        <GraviButton
          style={buttonStyle}
          appearance='outlined'
          theme2={isActive.sites}
          icon={<IconGasStation stroke={1.5} size={18} />}
        >
          Sites
          {isActive.sites && <FilterFilled size={16} className='ml-1' />}
        </GraviButton>
      </Dropdown>
      <Dropdown
        trigger={['click']}
        overlay={
          <FilterDropdown
            name='terminals'
            filterOptions={searchOptions}
            handleFilterChange={handleFilterChange}
            handleLocationSearch={handleLocationSearch}
            areFiltersActive={isActive.terminals}
            poiFilters={filters.terminals}
          />
        }
        placement='bottomLeft'
        className={isActive.terminals ? 'active-filter' : ''} // Add active class if filter is active
        destroyPopupOnHide={true} // Ensure dropdown is destroyed when hidden
      >
        <GraviButton
          style={buttonStyle}
          appearance='outlined'
          theme2={isActive.terminals}
          icon={<IconBuildingFactory2 stroke={1.5} size={18} />}
        >
          Terminals
          {isActive.terminals && <FilterFilled size={16} className='ml-1' />}
        </GraviButton>
      </Dropdown>
      <Dropdown
        trigger={['click']}
        overlay={
          <FilterDropdown
            name='depots'
            filterOptions={searchOptions}
            handleFilterChange={handleFilterChange}
            handleLocationSearch={handleLocationSearch}
            areFiltersActive={isActive.depots}
            poiFilters={filters.depots}
          />
        }
        placement='bottomLeft'
        className={isActive.depots ? 'active-filter' : ''} // Add active class if filter is active
        destroyPopupOnHide={true} // Ensure dropdown is destroyed when hidden
      >
        <GraviButton
          style={buttonStyle}
          appearance='outlined'
          theme2={isActive.depots}
          icon={<HomeOutlined style={{ fontSize: 14 }} />}
        >
          Depots
          {isActive.depots && <FilterFilled size={16} className='ml-1' />}
        </GraviButton>
      </Dropdown>
      <Dropdown
        trigger={['click']}
        placement='bottomLeft'
        overlay={<SettingsDropdown settings={settings} handleUpdateMapSettings={handleUpdateMapSettings} />}
      >
        {/* Settings button for additional options */}
        <GraviButton style={buttonStyle} appearance='outlined' icon={<IconSettings stroke={1.5} size={18} />} />
      </Dropdown>
    </Horizontal>
  )
}
